import { addToast } from "@caprikorn/dynamo-ui";
import type { FetchiosError, FetchiosResponse } from "@caprikorn/dynamo-utils";
import { useMutation } from "@tanstack/react-query";

import { queryClient } from "@/AppQueryClient";
import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { ERROR_CATEGORIES } from "@services/apiEcms/apiEcms.constants";
import type {
  PostMeterReadingErrorResponse,
  PostMeterReadingParams,
  PostMeterReadingSuccessResponse,
  TemporalCategoryShortName,
} from "@services/apiEcms/apiEcms.types";
import { apiEcmsHttp } from "@services/http";
import { QUERY_KEYS } from "@services/keys";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

const postMeterReading = ({
  indexes,
  customerId,
  contractId,
  siteId,
}: PostMeterReadingParams) =>
  apiEcmsHttp.post<PostMeterReadingSuccessResponse>("/meter-reading", {
    indexes,
    customerId,
    contractId,
    siteId,
  });

export const usePostMeterReading = (
  customerId: string,
  contractId: string,
  siteId: string,
) => {
  const { currentGroup } = useGroupReferences();
  const { t } = useCustomTranslation("main");

  return useMutation<
    FetchiosResponse<PostMeterReadingSuccessResponse>,
    FetchiosError<PostMeterReadingErrorResponse>,
    Array<{ temporalCategory: TemporalCategoryShortName; value: number }>
  >({
    mutationFn: (indexes) =>
      postMeterReading({ indexes, customerId, contractId, siteId }),
    retry: false,
    mutationKey: [QUERY_KEYS.METER_READING_CREATION, currentGroup],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.EXPOSITION_POINT,
          customerId,
          contractId,
          siteId,
          currentGroup,
        ],
      });

      addToast({
        variant: "success",
        title: t("perimeter.selfReadingSuccess.title"),
        description: t("perimeter.selfReadingSuccess.description"),
      });
    },
    onError: (error) => {
      if (error.response?.data.type === ERROR_CATEGORIES.INDEX_TOO_LOW) {
        addToast({
          variant: "error",
          title: t("perimeter.selfReadingIndexTooLowError.title"),
          description: t("perimeter.selfReadingIndexTooLowError.description"),
        });

        return;
      }

      if (error.response?.data.type === ERROR_CATEGORIES.INDEX_TOO_HIGH) {
        addToast({
          variant: "error",
          title: t("perimeter.selfReadingIndexTooHighError.title"),
          description: t("perimeter.selfReadingIndexTooHighError.description"),
        });

        return;
      }

      addToast({
        variant: "error",
        title: t("perimeter.selfReadingUnknownError.title"),
        description: t("perimeter.selfReadingUnknownError.description"),
      });
    },
  });
};
